import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, tap, catchError, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { LoginData, PermisoSeccion, User } from '../models/user.model';
import {
  canUserAccessOpcionesAdmin,
  canUserDelete,
  canUserEdit,
  canUserView,
  isComercialUser,
  isSuperAdminUser,
} from '../utils/user-permissions.util';

const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiUrl = environment.apiUrl;

  private readonly currentUserSubject = new BehaviorSubject<User | null>(this.loadUserFromStorage());
  readonly currentUser$ = this.currentUserSubject.asObservable();

  login(email: string, password: string): Observable<LoginData> {
    return this.http
      .post<ApiResponse<LoginData>>(`${this.apiUrl}/auth/login`, {
        email,
        password,
        device_name: 'crm-spa',
      })
      .pipe(
        tap((response) => {
          this.setToken(response.data.access_token);
          this.setUser(response.data.user);
        }),
        map((response) => response.data),
      );
  }

  me(): Observable<User> {
    return this.http.get<ApiResponse<{ user: User }>>(`${this.apiUrl}/auth/me`).pipe(
      tap((response) => this.setUser(response.data.user)),
      map((response) => response.data.user),
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/logout`, {}).pipe(
      tap(() => this.clearSession()),
      catchError(() => {
        this.clearSession();
        return of(undefined);
      }),
      map(() => undefined),
    );
  }

  hydrateSession(): Observable<User | null> {
    if (!this.getToken()) {
      return of(null);
    }

    return this.me().pipe(catchError(() => {
      this.clearSession();
      return of(null);
    }));
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  updateCurrentUser(user: User): void {
    this.setUser(user);
  }

  isSuperAdmin(): boolean {
    return isSuperAdminUser(this.getCurrentUser());
  }

  isComercial(): boolean {
    return isComercialUser(this.getCurrentUser());
  }

  canView(seccion: PermisoSeccion | number): boolean {
    return canUserView(this.getCurrentUser(), seccion);
  }

  canEdit(seccion: PermisoSeccion | number): boolean {
    return canUserEdit(this.getCurrentUser(), seccion);
  }

  canDelete(seccion: PermisoSeccion | number): boolean {
    return canUserDelete(this.getCurrentUser(), seccion);
  }

  canAccessOpcionesAdmin(): boolean {
    return canUserAccessOpcionesAdmin(this.getCurrentUser());
  }

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.currentUserSubject.next(null);
    void this.router.navigate(['/login']);
  }

  private setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  private setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private loadUserFromStorage(): User | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as User;
    } catch {
      localStorage.removeItem(USER_KEY);
      return null;
    }
  }
}
